import React, { useState } from 'react';

import {
  Avatar,
  Box,
  Button,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { Flag, Logout as LogoutIcon, Profile } from 'iconsax-react';

import {
  ContentLoader,
  styled,
  useNavigate,
  useParams,
  useSelector,
  useTour,
  useTranslation,
} from 'third-party';

import { loginUrl, userProfileUrl } from 'constants/url';

import { useApp } from 'modules/app';
import { useUser } from 'modules/user';

import { appSelectors } from 'selectors';

import { getAvatarUrl } from 'utils/app';
import { deleteUserToken } from 'utils/auth';

import { APIUserSettingType } from 'types/backend';

import { palette } from 'styles/theme';

const StyledFilterButton = styled(Button)`
  border-radius: 10rem;
  color: rgba(255, 255, 255, 0.85) !important;
  font-weight: 400 !important;
  font-size: 0.8rem !important;
  text-transform: none !important;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

export const UserMenu = () => {
  const user = useSelector(appSelectors.user);
  const navigate = useNavigate();
  const { organizationId } = useParams();

  const { userLogOut } = useApp();
  const { updateUserSettings } = useUser();
  const { setIsOpen, setCurrentStep } = useTour();
  const { t } = useTranslation();
  const isNavigationCollapsed = useSelector(appSelectors.isNavigationCollapsed);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onLogOutClick = async () => {
    await userLogOut();
    deleteUserToken();

    // Clear all cookies
    document.cookie.split(';').forEach(function (c) {
      document.cookie = c
        .replace(/^ +/, '')
        .replace(/=.*/, '=;expires=' + new Date().toUTCString() + ';path=/');
    });

    navigate(loginUrl());
  };

  const onProfileClick = () => {
    navigate(userProfileUrl(organizationId));
  };

  const handleUserTourReset = () => {
    updateUserSettings({
      type: APIUserSettingType.UserTour,
      value: 'reset',
    });
    navigate('/');
    setCurrentStep(0);
    setIsOpen(true);
  };

  const isReady = user != null;

  return (
    <Box
      id="user-avatar"
      display="flex"
      alignItems="center"
      sx={{
        margin: '0 1rem 0.75rem 1rem',
        padding: '0.75rem 0 0 0.5rem',
      }}
    >
      <React.Fragment>
        {!isReady && (
          <Box pl={0.5}>
            <ContentLoader
              speed={2}
              width={30}
              height={30}
              viewBox="0 0 30 30"
              backgroundColor="#f3f3f3"
              foregroundColor="#ecebeb"
            >
              <circle cx="15" cy="15" r="15" />
            </ContentLoader>
          </Box>
        )}
        {isReady && (
          <Tooltip title="Account settings" arrow>
            <IconButton
              onClick={handleClick}
              size="small"
              aria-controls={open ? 'account-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
            >
              <Avatar
                src={getAvatarUrl(user)}
                sx={{
                  width: '1.6rem',
                  height: '1.6rem',
                  fontSize: '0.6rem',
                  bgcolor: 'rgba(255,255,255,0.2)',
                  cursor: 'pointer',
                  textTransform: 'uppercase',
                }}
                sizes="small"
              />
            </IconButton>
          </Tooltip>
        )}

        {!isNavigationCollapsed && isReady && (
          <StyledFilterButton variant="text" onClick={handleClick}>
            {user?.firstName} {user?.lastName}
          </StyledFilterButton>
        )}

        {!isNavigationCollapsed && !isReady && (
          <Box pl={2}>
            <ContentLoader
              speed={2}
              width={100}
              height={34}
              viewBox="0 0 100 34"
              backgroundColor="#f3f3f3"
              foregroundColor="#ecebeb"
            >
              <rect x="0" y="3" rx="0" ry="4" width="100" height="28" />
            </ContentLoader>
          </Box>
        )}

        {isReady && (
          <Menu
            anchorEl={anchorEl}
            id="account-menu"
            open={open}
            onClose={handleClose}
            onClick={handleClose}
            slotProps={{
              paper: {
                elevation: 0,
                sx: {
                  overflow: 'visible',
                  filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                  mb: 1.5,
                  '& .MuiAvatar-root': {
                    width: 32,
                    height: 32,
                    ml: -0.5,
                    mr: 1,
                  },
                  '&::before': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    bottom: 0,
                    right: 14,
                    width: 10,
                    height: 10,
                    bgcolor: 'background.paper',
                    transform: 'translateY(50%) rotate(45deg)',
                    zIndex: 0,
                  },
                },
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
          >
            <MenuItem
              onClick={() => {
                handleClose();
                onProfileClick();
              }}
            >
              <ListItemIcon>
                <Profile
                  variant="TwoTone"
                  color={palette.primary900}
                  size="20"
                />
              </ListItemIcon>
              {t('menu.profile')}
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleClose();
                handleUserTourReset();
              }}
            >
              <ListItemIcon>
                <Flag variant="TwoTone" color={palette.primary900} size="20" />
              </ListItemIcon>
              {t('menu.tour')}
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={() => {
                handleClose();
                onLogOutClick();
              }}
            >
              <ListItemIcon>
                <LogoutIcon
                  variant="TwoTone"
                  color={palette.primary900}
                  size="20"
                />
              </ListItemIcon>
              {t('menu.logOut')}
            </MenuItem>
          </Menu>
        )}
      </React.Fragment>
    </Box>
  );
};
